
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
// Image removed - use img tag;
import { Link } from "react-router-dom";
import { categoriesApi } from "@/lib/api";

interface Category {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  image_url: string | null;
  icon: string | null;
  is_active: boolean;
  display_order: number;
}

export default function ShopByCategory() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await categoriesApi.getAll();
        const categoriesData = response.categories || [];
        setCategories(categoriesData);
        console.log("Categories loaded:", categoriesData.length);
      } catch (error: any) {
        console.error("Failed to fetch categories:", error);
        setError(error?.message || "Failed to load categories");
        // Keep empty array to show section even on error
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  if (loading) {
  return (
    <section id="shop-by-category" className="py-12 bg-gradient-to-b from-white to-gray-50 mt-4 mb-4 scroll-mt-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold font-playfair mb-3">
              Shop by <span className="gradient-text">Category</span>
            </h2>
            <p className="text-gray-600 text-base max-w-2xl mx-auto">
              Explore our wide range of categories
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-6">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="animate-pulse flex flex-col items-center">
                <div className="w-32 h-32 bg-gray-200 rounded-full mb-3" />
                <div className="h-4 bg-gray-200 rounded w-20" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Show section even if no categories (for debugging)
  // if (categories.length === 0) {
  //   return null;
  // }

  return (
    <section id="shop-by-category" className="py-12 bg-gradient-to-b from-white to-gray-50 mt-4 mb-4 scroll-mt-24">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h2 className="text-3xl md:text-4xl font-bold font-playfair mb-3">
            Shop by <span className="gradient-text">Category</span>
          </h2>
          <p className="text-gray-600 text-base max-w-2xl mx-auto">
            Explore our wide range of categories
          </p>
        </motion.div>
        {error && (
          <div className="text-center text-red-600 mb-4">
            Error: {error}
          </div>
        )}
        {categories.length === 0 && !loading && (
          <div className="text-center text-gray-500 mb-4">
            No categories available
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6 max-w-6xl mx-auto">
          {categories.map((category, index) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Link
                to={`/products?category=${category.slug}`}
                className="group block"
              >
                <motion.div
                  whileHover={{ y: -8 }}
                  className="relative aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-aarsa-cream-200 via-aarsa-cream-100 to-aarsa-sage-50 shadow-md hover:shadow-xl transition-all duration-300"
                >
                  {/* Background Image or Gradient */}
                  {category.image_url ? (
                    <img
                      src={category.image_url}
                      alt={category.name}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-aarsa-gold-200 via-aarsa-gold-100 to-aarsa-sage-200"></div>
                  )}
                  
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent group-hover:from-black/70 transition-all duration-300"></div>
                  
                  {/* Icon */}
                  {category.icon && (
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                      <span className="text-5xl text-white drop-shadow-lg group-hover:scale-110 transition-transform duration-300">
                        {category.icon}
                      </span>
                    </div>
                  )}
                  
                  {/* Category Name at Bottom */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 z-10">
                    <h3 className="text-white font-bold text-sm md:text-base text-center drop-shadow-lg group-hover:translate-y-[-2px] transition-transform duration-300">
                      {category.name}
                    </h3>
                  </div>
                  
                  {/* Shine Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                </motion.div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
